import * as Sentry from '@sentry/vue'
import axios from '@/services/axios.js'
import { currencyMap, headers } from '@/variables.js'
import { getConfig } from '@/api/config'
import { useGeneralStore } from '@/store/pinia/generalStore'
import baseURLs from '@/api/APIBaseURLs'
import { useCubesStore } from '@/store/pinia/cubesStore'
import { FormatStringTimestamp } from '@/libs/date_utils'
import * as tokenService from '@/auth/tokenService'

class Auth {
  azureLogin (token) {
    return this.socialLogin(token, 'azure')
  }

  googleLogin (token) {
    return this.socialLogin(token, 'google')
  }

  socialLogin (token, provider) {
    return axios.post(`${baseURLs.manageAPI}/auth/${provider}`, { token, org_key: this.getOrgKey() }, { headers: headers })
      .then(this._loginCallback.bind(this))
  }

  _loginCallback (response) {
    tokenService.setTokens({
      access_token: response.data.access_token,
      expiry: response.data.expiry,
      refresh_token: response.data.refresh_token,
    })
    this.setIdentityData(response.data.identity, response.data.organisations)
    return response
  }

  isAccessTokenValid () {
    return tokenService.isAccessTokenValid()
  }

  checkFrontendVersion (newVersion) {
    const currentVersion = localStorage.getItem('fe_version')
    if (currentVersion === newVersion || !currentVersion || !newVersion) {
      return
    }
    localStorage.setItem('fe_version', newVersion)
    this.logout().then(() => {
      window.location.reload()
    })
  }

  getOrganisations () {
    try {
      return JSON.parse(localStorage.getItem('organisations')) || []
    } catch (e) {
      console.error(e)
    }
    return []
  }

  setIdentityData (identity, organisations) {
    if (organisations) {
      localStorage.setItem('organisations', JSON.stringify(organisations))
    }
    this.checkFrontendVersion(identity.fe_version)
    localStorage.setItem('email', identity.email)
    localStorage.setItem('superuser', identity.superuser)
    localStorage.setItem('user_id', identity.id)
    localStorage.setItem('org', identity.organisation)
    localStorage.setItem('org_key', identity.org_key)
    localStorage.setItem('user_access_stock', identity.user_access.stock)
    localStorage.setItem('user_access_buy', identity.user_access.buy)
    localStorage.setItem('user_access_settings', identity.user_access.settings ?? '')
    localStorage.setItem('identity_vat', identity.vat)
    localStorage.setItem('fe_version', identity.fe_version)
    localStorage.setItem('logicApiVersion', identity.version)
    localStorage.setItem('defaultCurrency', identity.currency)
    localStorage.setItem('identity_vat', identity.vat)
    localStorage.setItem('defaultCurrency', identity.currency)

    try {
      Sentry.setUser({
        email: identity.email,
        id: identity.id,
        org: identity.organisation,
        org_key: identity.org_key,
        superuser: identity.superuser,
      })
    } catch (e) {
      console.error('Sentry setting user failed', e)
    }
  }

  login (email, pass) {
    const credentials = {
      email: email,
      password: pass,
      org_key: this.getOrgKey(),
    }
    return axios.post(baseURLs.manageAPI + '/login', credentials, { headers: headers })
      .then(this._loginCallback.bind(this))
      .catch(error => Promise.reject(error.response))
  }

  setAccessToken (token, expiry) {
    tokenService.setTokens({ access_token: token, expiry })
  }

  /**
   * Refresh the access token. Deduped and identity-syncing is handled by
   * tokenService (see the setOnRefreshed hook at the bottom of this file).
   * @returns {Promise<import('@/auth/tokenService').RefreshResult>}
   */
  refreshTokens () {
    return tokenService.refresh()
  }

  deleteAppConfig () {
    localStorage.removeItem('appConfig')
    localStorage.removeItem('appPartialConfigLastUpdate')
    localStorage.removeItem('appFullConfigLastUpdate')
  }

  async initAppConfig () {
    const appConfig = JSON.parse(localStorage.getItem('appConfig'))
    const userId = JSON.parse(localStorage.getItem('user_id'))

    if (!appConfig && userId) {
      await this.refreshAppConfig()
    } else {
      useGeneralStore().setAppConfig(appConfig || { fe_settings: {} })
    }
  }

  async refreshPartialConfig () {
    const lastUpdate = localStorage.getItem('appPartialConfigLastUpdate') || localStorage.getItem('appFullConfigLastUpdate')
    let config = {}
    try {
      config = JSON.parse(localStorage.getItem('appConfig') || '{}') || {}
    } catch (e) {
      console.error(e)
    }
    try {
      const newConfig = await getConfig(FormatStringTimestamp(new Date(lastUpdate)))
      if(Object.keys(newConfig.data)?.length) {
        config = Object.assign(config, newConfig.data)
        useGeneralStore().setAppConfig(config)
        localStorage.setItem('appConfig', JSON.stringify(config))
        localStorage.setItem('appPartialConfigLastUpdate', new Date())
        return newConfig.data
      }
    } catch (e) {
      console.error(e)
    }
  }

  async checkAppFullConfigExpiration () {
    const now = new Date()
    let lastUpdate = localStorage.getItem('appFullConfigLastUpdate')

    lastUpdate = lastUpdate ? new Date(lastUpdate) : null

    // if last update was more than 0.5 hour ago
    if (!lastUpdate || (now - lastUpdate >= 1000 * 1800)) {
      return this.refreshAppConfig()
    } else {
      return Promise.resolve()
    }
  }
  async checkAppPartialConfigExpiration () {
    const now = new Date()
    let lastUpdate = localStorage.getItem('appPartialConfigLastUpdate')

    lastUpdate = lastUpdate ? new Date(lastUpdate) : null
    // if last update was more than 0.5 hour ago
    if (!lastUpdate || (now - lastUpdate >= 1000 * 300)) {
      return this.refreshPartialConfig()
    } else {
      return Promise.resolve()
    }
  }

  async refreshAppConfig (ts = null, keys = []) {
    let config = {}
    // Separate try-catch blocks to avoid not loading the app if json in LS is corrupted
    try {
      config = JSON.parse(localStorage.getItem('appConfig') || '{}') || {}
    } catch (e) {
      console.error(e)
    }
    try {
      const newConfig = await getConfig(ts, keys)
      config = Object.assign(config, newConfig.data)
    } catch (e) {
      console.error(e)
    }

    useGeneralStore().setAppConfig(config)
    try {
      localStorage.setItem('appConfig', JSON.stringify(config))
      localStorage.setItem('appFullConfigLastUpdate', (new Date()).toString())
    } catch (e) {
      console.error('Failed to persist appConfig to localStorage', e)
    }
  }

  clearLocalStorage (logout = false) {
    const access_token = localStorage.getItem(tokenService.LS_ACCESS_TOKEN)
    const refresh_token = localStorage.getItem(tokenService.LS_REFRESH_TOKEN)
    const org_key = localStorage.getItem('org_key')
    const expires_at = localStorage.getItem(tokenService.LS_EXPIRES_AT)
    const devMode = localStorage.getItem('devMode')
    const version = localStorage.getItem('fe_version')
    const lastVisitedModule = localStorage.getItem('lastVisitedModule')
    const currency = localStorage.getItem('defaultCurrency')
    // Clear everything in localStorage
    localStorage.clear()
    // Refresh everything in localStorage except tokens
    localStorage.setItem('devMode', devMode)
    localStorage.setItem('lastVisitedModule', lastVisitedModule)
    localStorage.setItem('fe_version', version)
    localStorage.setItem('defaultCurrency', currency)
    localStorage.setItem('org_key', org_key)
    if (!logout) {
      // if we are not logging out, we need to set the tokens back
      localStorage.setItem(tokenService.LS_ACCESS_TOKEN, access_token)
      localStorage.setItem(tokenService.LS_REFRESH_TOKEN, refresh_token)
      localStorage.setItem(tokenService.LS_EXPIRES_AT, expires_at)
    }
  }

  logout () {
    // Bump generation first so any in-flight refresh can't resurrect tokens.
    tokenService.invalidateInflight()
    this.clearLocalStorage(true)
    useCubesStore().$reset()
    const url = baseURLs.manageAPI + '/logout'

    // The in-memory axios Authorization header still holds the access token, so
    // the logout call is authenticated; clear it once the request settles.
    return axios.get(url)
      .then(() => tokenService.clearTokens())
      .catch((e) => {
        tokenService.clearTokens()
        console.error(e)
      })
  }

  getUserCurrency () {
    const currency = localStorage.getItem('defaultCurrency')

    return {
      currency,
      symbol: currencyMap[currency],
    }
  }
  resetPassword (id, token, password) {
    return axios.put(baseURLs.manageAPI + '/change', { id, token, password }, { headers: headers })
      .then(response => {
        return response
      })
      .catch(error => {
        if (error.response) {
          return error.response
        }
      })
  }

  forgotPassword (email) {
    return axios.put(baseURLs.manageAPI + '/forgot', { email }, { headers })
      .then(response => {
        return response
      })
      .catch(error => {
        if (error.response) {
          return error.response
        }
      })
  }

  async setOrgKey (org_key) {
    localStorage.setItem('org_key', org_key)
    try {
      const response = await axios.post(baseURLs.manageAPI + '/switch', { org_key })
      this.clearLocalStorage(false)
      this._loginCallback(response)
      window.location.reload()
    } catch (e) {
      console.error(e)
    }
  }

  async ResetCurrentOrg () {
    const org_key = this.getOrgKey()
    const response = await axios.post(baseURLs.manageAPI + '/switch', { org_key })
    this.clearLocalStorage(false)
    this._loginCallback(response)
    await this.refreshAppConfig()
    setTimeout(() => {
      window.location = '/'
    }, 500)
  }

  getOrgKey () {
    return localStorage.getItem('org_key') || 0
  }
}

const authService = new Auth()

// Keep identity/org data fresh after every token refresh (reactive or proactive).
tokenService.setOnRefreshed((response) => {
  if (response?.data?.identity) {
    authService.setIdentityData(response.data.identity)
  }
})

export default authService
